import { UserCog, ListTodo } from "lucide-react";
import React, { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import {
  selectPartiesInvolvedError,
  selectPartiesInvolvedStatus,
} from "../../partiesinvolved/logic/PartiesInvolvedSelectors";
import {
  selectCurrentRecommendation,
  selectRecommendationsError,
  selectRecommendationStatus,
} from "../logic/Slice";
import { getRecommendationsThunk } from "../logic/Thunk";
import ActionsAgainstInvolvedPartiesTable from "./ActionAgainstInvolvedPartiesTable";
import ActionsAgainstInvolvedPartiesTableV2 from "./ActionAgainstInvolvedPartiesTable/ActionsAgainstInvolvedPartiesTableV2";
import PolicyImprovementsTable from "./PolicyImprovementsTable";
import RecommendationsContainer from "./Recommendations";
import TableContentWrapper from "./TableContentWrapper";
import { RETRIEVING_DATA_SUBTITLE } from "../../../lib/constants";
import { isCreditAccountStakeholdersRevampEnabled, isReportsPolicyNormalizationEnabled } from "../../configs/logic/Slice";
import PolicyImprovementsTableV2 from "./PolicyImprovementsTable/PolicyImprovementsTableV2";

const CaseRecommendationsContent: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const recommendations = useAppSelector((state) =>
    selectCurrentRecommendation(state, caseId)
  );
  const status = useAppSelector((state) =>
    selectRecommendationStatus(state, caseId)
  );
  const error = useAppSelector((state) =>
    selectRecommendationsError(state, caseId)
  );
  const partyInvolvedStatus = useAppSelector(selectPartiesInvolvedStatus);
  const partyInvolvedError = useAppSelector(selectPartiesInvolvedError);
  const isPolicyImprovementV2Enabled = useAppSelector(isReportsPolicyNormalizationEnabled);
  const isCreditAccountStakeholdersRevamp = useAppSelector(isCreditAccountStakeholdersRevampEnabled);

  const isLoading = status === "loading";

  const handleRetry = useCallback(() => {
    if (caseId) {
      dispatch(getRecommendationsThunk({ case_id: caseId }));
    }
  }, [caseId, dispatch]);

  if (!caseId) {
    return (
      <ErrorDisplay title="Case ID not found in URL" showRetryButton={false} />
    );
  }

  if (isLoading && !recommendations) {
    return (
      <CustomLoadingSpinner
        title="AI Agent is working..."
        subtitle="Please wait till the agent completes its tasks."
        minHeight="min-h-[400px]"
      />
    );
  }

  if (status === "failed") {
    return (
      <ErrorDisplay
        title="Failed to Load Recommendations"
        error={error}
        onRetry={handleRetry}
      />
    );
  }

  const renderActionsAgainstInvolvedPartiesV2 = () => (
    <TableContentWrapper
      showTopDivider
      showSubheader
      headerIcon={
        <UserCog className="w-[16.4167px] h-[15px] text-[#4B5563]" />
      }
      headerTitle="Actions Against Involved Parties"
      subheaderTitle="Manage actions and their implementation status for involved parties"
    >
      <ActionsAgainstInvolvedPartiesTableV2 caseId={caseId} />
    </TableContentWrapper>
  );

  const renderPartyInvolvedLoadingState = () =>
    partyInvolvedStatus === "loading" && (
      <div className="mt-2">
        <CustomLoadingSpinner
          title="Loading Recommended Action Against Parties..."
          subtitle={RETRIEVING_DATA_SUBTITLE}
          minHeight="min-h-[100px]"
        />
      </div>
    );

  const renderPartyInvolvedFailedState = () =>
    partyInvolvedStatus === "failed" && (
      <ErrorDisplay
        title="Failed to load parties involved"
        error={partyInvolvedError}
      />
    );

  const renderPartyInvolvedSucceededState = () =>
    partyInvolvedStatus === "succeeded" && (
      <TableContentWrapper
        showTopDivider
        showSubheader
        headerIcon={
          <UserCog className="w-[16.4167px] h-[15px] text-[#4B5563]" />
        }
        headerTitle="Actions Against Involved Parties"
        subheaderTitle="Manage actions and their implementation status for involved parties"
      >
        <ActionsAgainstInvolvedPartiesTable caseId={caseId} />
      </TableContentWrapper>
    );

  const renderActionAgainstPartyInvolvedTable = () =>
    isCreditAccountStakeholdersRevamp ? (
      renderActionsAgainstInvolvedPartiesV2() //spinner and error state is defined inside of the component
    ) : (
      <>
        {renderPartyInvolvedLoadingState()}
        {renderPartyInvolvedFailedState()}
        {renderPartyInvolvedSucceededState()}
      </>
    );

  const renderPolicyImprovementsTable = () => {
    if (isPolicyImprovementV2Enabled) {
      return (
        <TableContentWrapper
          showTopDivider
          showSubheader
          headerIcon={
            <ListTodo className="w-[16.4167px] h-[15px] text-[#4B5563]" />
          }
          headerTitle="Process & Policy Improvements"
          subheaderTitle="Track suggested policy changes and their approval status"
        >
          <PolicyImprovementsTableV2 caseId={caseId} />
        </TableContentWrapper>
      )
    }

    if (status === "loading") {
      return (
        <div className="mt-2">
          <CustomLoadingSpinner
            title="Loading Policy Improvements..."
            subtitle={RETRIEVING_DATA_SUBTITLE}
            minHeight="min-h-[100px]"
          />
        </div>
      );
    }

    if (status === "succeeded") {
      return (
        <TableContentWrapper
          showTopDivider
          showSubheader
          headerIcon={
            <ListTodo className="w-[16.4167px] h-[15px] text-[#4B5563]" />
          }
          headerTitle="Process & Policy Improvements"
          subheaderTitle="Track suggested policy changes and their approval status"
        >
          <PolicyImprovementsTable caseId={caseId} />
        </TableContentWrapper>
      );
    }

    return null;
  };

  const renderRecommendationsContainer = () => (
    <div className="mt-1">
      <RecommendationsContainer />
    </div>
  );

  return (
    <div>
      <div className="px-12 pt-6">
        <span className="text-xl font-semibold text-neutral-900">
          Recommendations
        </span>
      </div>
      <div className="flex flex-col gap-6 px-12 py-2 pb-16">
        {renderRecommendationsContainer()}
        {renderActionAgainstPartyInvolvedTable()}
        {renderPolicyImprovementsTable()}
      </div>
    </div>
  );
};

export default CaseRecommendationsContent;